<template>
  <div class="container mx-auto px-4 py-4">
    <div class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      <div class="p-6 border-b border-gray-200 dark:border-gray-700">
        <div class="flex justify-between items-center">
          <div>
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">Songs</h1>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Total: {{ songs.length }} songs
            </p>
          </div>
          <Button label="Add New Song" severity="primary" icon="pi pi-plus" @click="openAddModal" />
        </div>
      </div>

      <div class="p-6">
        <div v-if="loading" class="text-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>

        <div v-else-if="songs.length === 0" class="text-center py-8">
          <p class="text-gray-500 dark:text-gray-400">
            No songs found. Add your first song to get started!
          </p>
        </div>

        <div v-else>
          <!-- Search Bar -->
          <div class="mb-4">
            <IconField icon-position="left">
              <InputIcon class="pi pi-search" />
              <InputText
                v-model="globalFilter"
                placeholder="Search songs by title, artist, genre, year, or categories..."
                class="w-full border border-gray-300 dark:border-gray-600"
              />
            </IconField>
          </div>

          <DataTable
            :value="filteredSongs"
            :loading="loading"
            striped-rows
            paginator
            :rows="rows"
            :rows-per-page-options="[10, 25, 50, 100]"
            class="p-datatable-sm"
            :pt="{
              root: { class: 'border border-gray-200 rounded-lg' }
            }"
          >
            <Column field="title" header="Title" sortable>
              <template #body="{ data }">
                <div class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ data.title }}
                </div>
              </template>
            </Column>

            <Column field="author" header="Artist" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-gray-200">{{ data.author }}</div>
              </template>
            </Column>

            <Column field="genre" header="Genre" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-gray-200">{{ data.genre || "-" }}</div>
              </template>
            </Column>

            <Column field="category" header="Categories" sortable>
              <template #body="{ data }">
                <div class="text-sm text-gray-900 dark:text-gray-200">
                  <span
                    v-if="
                      data.categories &&
                      Array.isArray(data.categories) &&
                      data.categories.length > 0
                    "
                  >
                    {{ data.categories.join(", ") }}
                  </span>
                  <span v-else-if="data.category">{{ data.category }}</span>
                  <span v-else>-</span>
                </div>
              </template>
            </Column>

            <Column field="isDisabled" header="Status">
              <template #body="{ data }">
                <ToggleSwitch
                  :model-value="!data.isDisabled"
                  :disabled="togglingSongId === data.id"
                  :class="data.isDisabled ? 'song-toggle-disabled' : ''"
                  @update:model-value="(value) => toggleSongStatus(data, !value)"
                />
              </template>
            </Column>

            <Column header="" :exportable="false" style="width: 100px">
              <template #body="{ data }">
                <div class="flex items-center gap-2">
                  <button
                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    title="Edit song"
                    @click="openEditModal(data)"
                  >
                    <PencilIcon class="h-5 w-5" />
                  </button>
                  <button
                    :disabled="deletingSongId === data.id"
                    class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete song"
                    @click="handleDeleteSong(data)"
                  >
                    <TrashIcon class="h-5 w-5" />
                  </button>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- Add/Edit Song Modal -->
    <div
      v-if="showSongModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto"
      @click.self="handleOutsideClick"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto"
      >
        <div class="p-6">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
              {{ editingSong ? "Edit Song" : "Add New Song" }}
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {{ editingSong ? "Update song information" : "Add a new song to the library" }}
            </p>
          </div>

          <form class="space-y-4" @submit.prevent="handleSaveSong">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Row 1: Title (full width) -->
              <div class="md:col-span-2">
                <label
                  for="song-title"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >Title *</label
                >
                <InputText
                  id="song-title"
                  v-model="songForm.title"
                  required
                  placeholder="Enter song title"
                  class="w-full"
                />
              </div>

              <!-- Row 2: Artist | Song Length -->
              <div>
                <label
                  for="song-author"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >Artist *</label
                >
                <InputText
                  id="song-author"
                  v-model="songForm.author"
                  required
                  placeholder="Enter artist"
                  class="w-full"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >Song Length</label
                >
                <div class="flex items-center gap-2">
                  <InputNumber
                    v-model="songForm.songLengthMin"
                    :min="0"
                    :max="999"
                    placeholder="0"
                    :use-grouping="false"
                    input-class="w-16 text-center"
                  />
                  <span class="text-gray-500 dark:text-gray-400 font-semibold text-lg">:</span>
                  <InputNumber
                    v-model="songForm.songLengthSec"
                    :min="0"
                    :max="59"
                    placeholder="00"
                    :use-grouping="false"
                    input-class="w-16 text-center"
                  />
                  <span class="text-sm text-gray-400 dark:text-gray-500">mm : ss</span>
                </div>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Used by the setlist feature to calculate set duration
                </p>
              </div>

              <!-- Row 3: Categories | Genre -->
              <div>
                <label
                  for="song-category"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >Categories</label
                >
                <MultiSelect
                  id="song-category"
                  v-model="songForm.categories"
                  :options="categories"
                  option-label="name"
                  option-value="name"
                  display="chip"
                  placeholder="Select categories (optional)"
                  class="w-full"
                />
              </div>

              <div>
                <label
                  for="song-genre"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >Genre</label
                >
                <Select
                  id="song-genre"
                  v-model="songForm.genre"
                  :options="genres"
                  option-label="name"
                  option-value="name"
                  placeholder="Select a genre (optional)"
                  class="w-full"
                  :filter="true"
                  filter-placeholder="Search genres"
                  :show-clear="true"
                />
              </div>

              <!-- Separator: fields below are for internal use -->
              <div class="md:col-span-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                <p
                  class="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide font-medium"
                >
                  Own use only
                </p>
              </div>

              <!-- Row 4: Language -->
              <div>
                <label
                  for="song-language"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >Language</label
                >
                <InputText
                  id="song-language"
                  v-model="songForm.language"
                  placeholder="Enter language (optional)"
                  class="w-full"
                />
              </div>
            </div>

            <!-- Row 5: Notes (full width) -->
            <div>
              <label
                for="song-notes"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                >Notes</label
              >
              <Textarea
                id="song-notes"
                v-model="songForm.notes"
                rows="3"
                placeholder="Enter optional notes about the song"
                class="w-full"
              />
            </div>

            <!-- Separator -->
            <div class="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4"></div>

            <!-- Status Toggle -->
            <div class="flex items-center justify-between">
              <div>
                <label
                  for="song-isDisabled"
                  class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >Song Status</label
                >
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {{
                    songForm.isDisabled
                      ? "Song is disabled and will not be visible for voting"
                      : "Song is active and available for voting"
                  }}
                </p>
              </div>
              <div class="flex items-center">
                <span
                  class="mr-3 text-sm font-medium"
                  :class="songForm.isDisabled ? 'text-red-600' : 'text-green-600'"
                >
                  {{ songForm.isDisabled ? "Disabled" : "Active" }}
                </span>
                <button
                  type="button"
                  :class="[
                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                    songForm.isDisabled ? 'bg-red-600' : 'bg-gray-200'
                  ]"
                  @click="songForm.isDisabled = !songForm.isDisabled"
                >
                  <span
                    :class="[
                      'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                      songForm.isDisabled ? 'translate-x-5' : 'translate-x-0'
                    ]"
                  />
                </button>
              </div>
            </div>

            <div
              v-if="songError"
              class="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3"
            >
              {{ songError }}
            </div>

            <div
              class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700"
            >
              <Button type="button" label="Cancel" severity="secondary" @click="cancelSongModal" />
              <Button
                type="submit"
                :disabled="songLoading"
                :label="songLoading ? 'Saving...' : 'Save'"
                severity="primary"
                :loading="songLoading"
              />
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelDelete"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4"
      >
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div
              class="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20"
            >
              <svg
                class="h-6 w-6 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white text-center mb-2">
            Delete Song
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            Are you sure you want to delete <strong>{{ songToDelete?.title }}</strong> by
            <strong>{{ songToDelete?.author }}</strong
            >? This action cannot be undone.
          </p>
          <div class="flex space-x-3">
            <button class="btn btn-secondary flex-1" @click="cancelDelete">Cancel</button>
            <button
              :disabled="deletingSongId !== null"
              class="btn btn-danger flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="confirmDelete"
            >
              {{ deletingSongId !== null ? "Deleting..." : "Delete" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import ToggleSwitch from "primevue/toggleswitch";
import MultiSelect from "primevue/multiselect";
import Select from "primevue/select";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Textarea from "primevue/textarea";
import Button from "primevue/button";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import { PencilIcon, TrashIcon } from "@heroicons/vue/24/outline";
import api from "../../services/api";

export default {
  name: "AdminSongs",
  components: {
    DataTable,
    Column,
    ToggleSwitch,
    MultiSelect,
    Select,
    InputText,
    InputNumber,
    Textarea,
    Button,
    IconField,
    InputIcon,
    PencilIcon,
    TrashIcon
  },
  setup() {
    const toast = useToast();
    const confirm = useConfirm();
    const songs = ref([]);
    const loading = ref(true);
    const categories = ref([]);
    const genres = ref([]);
    const showSongModal = ref(false);
    const rows = ref(10);
    const globalFilter = ref("");
    const editingSong = ref(null);
    const songForm = ref({
      title: "",
      author: "",
      genre: "",
      songLengthMin: null,
      songLengthSec: null,
      categories: [],
      language: "",
      notes: "",
      isDisabled: false
    });

    // Convert seconds (integer) → { min, sec } for the two InputNumber fields
    const secondsToMinSec = (totalSeconds) => {
      if (totalSeconds === null || totalSeconds === undefined) return { min: null, sec: null };
      return { min: Math.floor(totalSeconds / 60), sec: totalSeconds % 60 };
    };

    // Convert { min, sec } → seconds (integer), returns null if both are empty
    const minSecToSeconds = (min, sec) => {
      if ((min === null || min === undefined) && (sec === null || sec === undefined)) return null;
      return (min || 0) * 60 + (sec || 0);
    };
    const songError = ref("");
    const songLoading = ref(false);
    const togglingSongId = ref(null);
    const showDeleteModal = ref(false);
    const songToDelete = ref(null);
    const deletingSongId = ref(null);

    const fetchSongs = async () => {
      try {
        loading.value = true;
        const response = await api.get("/api/songs");
        if (response.data.success) {
          songs.value = response.data.data.songs || [];
        }
      } catch (error) {
        console.error("Failed to fetch songs:", error);
      } finally {
        loading.value = false;
      }
    };

    // Filtered songs based on globalFilter
    const filteredSongs = computed(() => {
      if (!globalFilter.value) {
        return songs.value;
      }

      const searchTerm = globalFilter.value.toLowerCase().trim();

      return songs.value.filter((song) => {
        // Search in title
        if (song.title?.toLowerCase().includes(searchTerm)) return true;

        // Search in author/artist
        if (song.author?.toLowerCase().includes(searchTerm)) return true;

        // Search in genre
        if (song.genre?.toLowerCase().includes(searchTerm)) return true;

        // Search in year
        if (song.year?.toString().includes(searchTerm)) return true;

        // Search in categories (array)
        if (song.categories && Array.isArray(song.categories)) {
          if (song.categories.some((cat) => cat?.toLowerCase().includes(searchTerm))) {
            return true;
          }
        }

        // Search in category (single string, for backward compatibility)
        if (song.category?.toLowerCase().includes(searchTerm)) return true;

        // Search in language
        if (song.language?.toLowerCase().includes(searchTerm)) return true;

        return false;
      });
    });

    const fetchCategories = async () => {
      try {
        const response = await api.get("/api/categories");
        if (response.data.success) {
          categories.value = response.data.data.categories || [];
        }
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    const fetchGenres = async () => {
      try {
        const response = await api.get("/api/genres");
        if (response.data.success) {
          genres.value = response.data.data.genres || [];
        }
      } catch (error) {
        console.error("Failed to fetch genres:", error);
      }
    };

    const openAddModal = () => {
      editingSong.value = null;
      songForm.value = {
        title: "",
        author: "",
        genre: "",
        songLengthMin: null,
        songLengthSec: null,
        categories: [],
        language: "",
        notes: "",
        isDisabled: false
      };
      songError.value = "";
      showSongModal.value = true;
    };

    const openEditModal = (song) => {
      editingSong.value = song;
      // Handle both old format (single category string) and new format (categories array)
      let categories = [];
      if (song.categories && Array.isArray(song.categories)) {
        categories = song.categories;
      } else if (song.category) {
        // Backward compatibility: if old single category exists, convert to array
        categories = [song.category];
      }
      const { min, sec } = secondsToMinSec(song.songLength);
      songForm.value = {
        title: song.title || "",
        author: song.author || "",
        genre: song.genre || "",
        songLengthMin: min,
        songLengthSec: sec,
        categories: categories,
        language: song.language || "",
        notes: song.notes || "",
        isDisabled: song.isDisabled || false
      };
      songError.value = "";
      showSongModal.value = true;
    };

    const cancelSongModal = () => {
      showSongModal.value = false;
      editingSong.value = null;
      songForm.value = {
        title: "",
        author: "",
        genre: "",
        songLengthMin: null,
        songLengthSec: null,
        categories: [],
        language: "",
        notes: "",
        isDisabled: false
      };
      songError.value = "";
    };

    const handleOutsideClick = () => {
      // Only show confirmation when adding a new song (not editing)
      if (!editingSong.value) {
        // Check if form has any data entered
        const hasFormData =
          songForm.value.title.trim() ||
          songForm.value.author.trim() ||
          songForm.value.genre.trim() ||
          songForm.value.songLengthMin !== null ||
          songForm.value.songLengthSec !== null ||
          (songForm.value.categories && songForm.value.categories.length > 0) ||
          songForm.value.language.trim() ||
          songForm.value.notes.trim();

        if (hasFormData) {
          confirm.require({
            message:
              "Are you sure you want to cancel adding this song? Any unsaved changes will be lost.",
            header: "Cancel Adding Song",
            icon: "pi pi-exclamation-triangle",
            rejectProps: {
              label: "No, Continue Editing",
              severity: "secondary",
              outlined: true
            },
            acceptProps: {
              label: "Yes, Cancel",
              severity: "danger"
            },
            accept: () => {
              cancelSongModal();
            },
            reject: () => {
              // User chose to continue editing, do nothing
            }
          });
        } else {
          // No data entered, close without confirmation
          cancelSongModal();
        }
      } else {
        // Editing mode - close without confirmation (existing behavior)
        cancelSongModal();
      }
    };

    const toggleSongStatus = async (song, newValue) => {
      try {
        togglingSongId.value = song.id;

        const response = await api.put(`/api/songs/${song.id}`, {
          isDisabled: newValue
        });

        if (response.data.success) {
          // Update the song in the local list
          const index = songs.value.findIndex((s) => s.id === song.id);
          if (index !== -1) {
            songs.value[index].isDisabled = newValue;
          }
        } else {
          console.error("Failed to toggle song status:", response.data.message);
          // Revert the change on error
          const index = songs.value.findIndex((s) => s.id === song.id);
          if (index !== -1) {
            songs.value[index].isDisabled = !newValue;
          }
        }
      } catch (err) {
        console.error("Toggle song status error:", err);
        // Revert the change on error
        const index = songs.value.findIndex((s) => s.id === song.id);
        if (index !== -1) {
          songs.value[index].isDisabled = !newValue;
        }
      } finally {
        togglingSongId.value = null;
      }
    };

    const handleDeleteSong = (song) => {
      songToDelete.value = song;
      showDeleteModal.value = true;
    };

    const cancelDelete = () => {
      showDeleteModal.value = false;
      songToDelete.value = null;
    };

    const confirmDelete = async () => {
      if (!songToDelete.value) return;

      try {
        deletingSongId.value = songToDelete.value.id;
        const response = await api.delete(`/api/songs/${songToDelete.value.id}`);

        if (response.data.success) {
          // Remove song from local list
          songs.value = songs.value.filter((s) => s.id !== songToDelete.value.id);
          showDeleteModal.value = false;
          songToDelete.value = null;
        } else {
          console.error("Failed to delete song:", response.data.message);
        }
      } catch (err) {
        console.error("Delete song error:", err);
      } finally {
        deletingSongId.value = null;
      }
    };

    const handleSaveSong = async () => {
      try {
        songLoading.value = true;
        songError.value = "";

        // Prepare data - convert empty strings to null for optional fields
        const songData = {
          title: songForm.value.title.trim(),
          author: songForm.value.author.trim(),
          genre: songForm.value.genre.trim() || null,
          songLength: minSecToSeconds(songForm.value.songLengthMin, songForm.value.songLengthSec),
          categories:
            songForm.value.categories && songForm.value.categories.length > 0
              ? songForm.value.categories
              : null,
          language: songForm.value.language.trim() || null,
          notes: songForm.value.notes.trim() || null,
          isDisabled: songForm.value.isDisabled
        };

        const url = editingSong.value ? `/api/songs/${editingSong.value.id}` : "/api/songs";

        const method = editingSong.value ? "put" : "post";

        const response = await api[method](url, songData);

        if (response.data.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: editingSong.value ? "Song updated successfully" : "Song created successfully",
            life: 3000
          });

          await fetchSongs();
          cancelSongModal();
        } else {
          songError.value = response.data.message || "Failed to save song";
        }
      } catch (err) {
        console.error("Save song error:", err);
        songError.value =
          err.response?.data?.message ||
          err.response?.data?.errors?.join(", ") ||
          "Failed to save song";
      } finally {
        songLoading.value = false;
      }
    };

    onMounted(() => {
      fetchSongs();
      fetchCategories();
      fetchGenres();
    });

    return {
      songs,
      loading,
      categories,
      genres,
      showSongModal,
      editingSong,
      songForm,
      songError,
      songLoading,
      togglingSongId,
      openAddModal,
      openEditModal,
      cancelSongModal,
      handleOutsideClick,
      handleSaveSong,
      toggleSongStatus,
      handleDeleteSong,
      cancelDelete,
      confirmDelete,
      showDeleteModal,
      songToDelete,
      deletingSongId,
      rows,
      globalFilter,
      filteredSongs
    };
  }
};
</script>

<style scoped>
/* Style ToggleSwitch OFF state (when song is disabled) to be red */
:deep(.song-toggle-disabled:not(.p-toggleswitch-checked) .p-toggleswitch-slider) {
  background-color: #ef4444 !important; /* red-500 */
}

/* Keep the toggle handle white */
:deep(.song-toggle-disabled .p-toggleswitch-slider:before) {
  background-color: white;
}
</style>
